import { useCallback, useEffect, useState } from "react";
import { CalendarPlus, Send, XCircle, CalendarClock } from "lucide-react";
import { toast } from "sonner";
import { http, errText } from "@/lib/api";
import { TimeFields, useTimeForm, MODE_LABEL, fieldClass } from "./TimeFields";

const statusText = (n) => Object.entries(n || {}).map(([k, s]) => `${k.replace(/_/g, " ")}: ${s}`).join(" · ");

export const InterviewCard = ({ iv, onCancel }) => (
  <div className={`p-4 rounded-2xl border ${iv.status === "scheduled" ? "border-[#154F49]/30 bg-white" : "border-dashed border-[#154F49]/20 bg-transparent opacity-80"}`} data-testid={`interview-${iv.id}`}>
    <div className="flex flex-wrap items-start justify-between gap-3">
      <div>
        <p className="font-semibold text-[#154F49]">{iv.when_label}</p>
        <p className="text-sm text-[#1A2E2B]">{MODE_LABEL[iv.mode]} · {iv.duration_min} min{iv.source === "applicant" ? " · booked by applicant" : ""}</p>
        {iv.video_link && <p className="text-xs text-[#465F57] break-all">{iv.video_link}</p>}
        {iv.notes && <p className="text-xs text-[#465F57]">Notes: {iv.notes}</p>}
        <p className="text-[11px] text-[#465F57] mt-1">{statusText(iv.notifications)}</p>
      </div>
      {iv.status === "scheduled" ? (
        onCancel && <button type="button" onClick={() => onCancel(iv)} className="btn-danger text-xs" data-testid={`cancel-interview-${iv.id}`}><XCircle className="w-4 h-4" /> Cancel</button>
      ) : <span className="px-2.5 py-1 rounded-full bg-red-50 text-red-800 text-xs font-bold">Cancelled</span>}
    </div>
  </div>
);

export const useCancel = (after) => async (iv) => {
  if (!window.confirm(`Cancel the interview on ${iv.when_label}? The applicant will be emailed.`)) return;
  try { await http.post(`/admin/interviews/${iv.id}/cancel`); toast.success("Interview cancelled and applicant notified."); after(); }
  catch (e) { toast.error(errText(e)); }
};

export const InterviewBox = ({ appId }) => {
  const [data, setData] = useState({ interviews: [], booking_link_sent: false });
  const [mode, setMode] = useState(null);
  const [v, set, reset] = useTimeForm();
  const [notes, setNotes] = useState("");
  const [busy, setBusy] = useState(false);
  const load = useCallback(() => http.get(`/admin/applications/${appId}/interview`).then((r) => setData({ interviews: Array.isArray(r.data?.interviews) ? r.data.interviews : [], booking_link_sent: !!r.data?.booking_link_sent })).catch(() => {}), [appId]);
  useEffect(() => { load(); return undefined; }, [load]);
  const cancel = useCancel(load);
  const active = data.interviews.find((i) => i.status === "scheduled");

  const schedule = async (e) => {
    e.preventDefault(); setBusy(true);
    try {
      const r = await http.post(`/admin/applications/${appId}/interview`, { ...v, notes });
      toast.success(r.data?.notifications?.invite_applicant === "sent" ? "Interview booked — invite emailed." : "Interview booked, but the email didn't send. Please contact the applicant.");
      reset(); setNotes(""); setMode(null); load();
    } catch (x) { toast.error(errText(x)); } finally { setBusy(false); }
  };
  const sendLink = async () => {
    setBusy(true);
    try { const r = await http.post(`/admin/applications/${appId}/booking-link`); toast.success(r.data?.email === "sent" ? "Booking link emailed to the applicant." : "Link created, but the email didn't send."); load(); }
    catch (x) { toast.error(errText(x)); } finally { setBusy(false); }
  };

  return (
    <section className="p-4 rounded-2xl border border-[#154F49]/20 bg-[#EDF1E6]/60 space-y-3" data-testid="interview-box">
      <p className="text-sm font-bold text-[#154F49] flex items-center gap-2"><CalendarClock className="w-4 h-4" /> Interview</p>
      {data.interviews.map((iv) => <InterviewCard key={iv.id} iv={iv} onCancel={cancel} />)}
      {!active && (
        <>
          {data.booking_link_sent && <p className="text-xs text-[#9B742A] font-semibold" data-testid="booking-link-pending">Booking link sent — waiting for the applicant to pick a time.</p>}
          <div className="flex flex-wrap gap-2">
            <button type="button" onClick={() => setMode(mode === "set" ? null : "set")} className="btn-primary text-sm" data-testid="interview-set-time-btn"><CalendarPlus className="w-4 h-4" /> Set a time</button>
            <button type="button" onClick={sendLink} disabled={busy} className="btn-ghost text-sm" data-testid="interview-send-link-btn"><Send className="w-4 h-4" /> {data.booking_link_sent ? "Resend booking link" : "Let applicant pick a slot"}</button>
          </div>
          {mode === "set" && (
            <form onSubmit={schedule} className="space-y-3" data-testid="interview-schedule-form">
              <TimeFields v={v} set={set} prefix="iv" />
              <label htmlFor="iv-notes" className="block space-y-1"><span className="text-xs font-semibold text-[#465F57]">Note for the applicant (optional)</span>
                <input id="iv-notes" value={notes} maxLength={500} onChange={(e) => setNotes(e.target.value)} placeholder="e.g. Ask for Ms. Johnson at the front desk" className={fieldClass} data-testid="iv-notes" /></label>
              <button type="submit" disabled={busy} className="btn-gold" data-testid="interview-confirm-btn">{busy ? "Booking…" : "Book & email invite"}</button>
              <p className="text-[11px] text-[#465F57]">The applicant and the office inbox get an email with a calendar invite. A reminder goes out at 9am the day before.</p>
            </form>
          )}
        </>
      )}
    </section>
  );
};
